import { SelectionChangedEvent } from "ag-grid-community";
import { StandardProposalItemEntity } from "domain/entity/StandardProposal/StandardProposalItemEntity";
import _ from "lodash";
import { approvalProcessStatusDropdownOption, proposalByDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { MessageConstant } from "presentation/constant/MessageConstant";
import { StandardProposalConstant, standardProposalRequiredFieldList } from "presentation/constant/StandardProposal/StandardProposalConstant";
import { INITIAL_STANDARD_PROPOSAL_DETAIL_COL_DEF, transferRowData } from "presentation/constant/StandardProposal/StandardProposalDetailColumnDefinition";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useStandardProposalVM } from "presentation/hook/StandardProposal/useStandardProposalVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useStandardProposalTracked } from "presentation/store/StandardProposal/StandardProposalProvider";
import { ConfirmDeleteModal } from "presentation/view/components/ConfirmDeleteModal";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import RightClickMenuWithMoveAfter from "presentation/view/components/NbisRightClickMenu/RightClickMenuWithMoveAfter";
import { OverflowMenuButton } from "presentation/view/components/OverflowMenuButton";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { createRef, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader, OverflowingSubMenu } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle, StyledAction } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";


const StandardProposalTariffItemTablePanel: React.FC = () => {
    const STANDARD_PROPOSAL_CONSTANT = StandardProposalConstant.Table;
    const [standardProposalState] = useStandardProposalTracked();
    const standardProposalVM = useStandardProposalVM();
    const { searchStatus, searchCriteria, standardProposalItemList, currentSelectedRow, tariffItemSelectedRows, isEditStdProposal, isMasterOrAlliance, isShowTariffItemTierPanel, deleteItemList, isAddIs, isAddTier, isEditIs, isEditTier, isAddTariffItem, isUpdateOrAdd } = standardProposalState;
    const gridRef: any = useRef(null);
    const { isSaveClicked, allFormState } = standardProposalState.stdProposalState;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { allowCreate, allowUpdate, allowDelete } = anaInfoState;
    const rightClickRef: any = createRef();
    // for right click menu with move after begin
    const [showMoveCursor, setShowMoveCursor] = useState<boolean>(false);
    const [allRows, setAllRows] = useState<StandardProposalItemEntity[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [initialAllRows, setInitialAllRows] = useState<boolean>(true);
    const [refreshAllRowsAfterDelete, setRefreshAllRowsAfterDelete] = useState<boolean>(false);
    const [isShowDelete, setIsShowDelete] = useState<boolean>(false);

    const getAllRows = useCallback(() => {
        return allRows;
    }, [allRows]);

    const getSelectedRows = useCallback(() => {
        return standardProposalState.tariffItemSelectedRows;
    }, [standardProposalState.tariffItemSelectedRows]);

    useEffect(() => {
        if (standardProposalState.initialProposalItem) {
            setInitialAllRows(true);
        }
    }, [standardProposalState.initialProposalItem]);

    useEffect(() => {
        const handleRightClickMenu = (event: any) => {
            if (_.isEmpty(standardProposalState.tariffItemSelectedRows) || !allowUpdate) {
                return;
            }
            event.preventDefault();
            rightClickRef.current.show(event);
        };
        document.getElementById('myDiv')?.addEventListener("contextmenu", handleRightClickMenu);

        return () => {
            document.getElementById('myDiv')?.removeEventListener("contextmenu", handleRightClickMenu);
        };
    }, [messageBarVM, rightClickRef, allowUpdate, standardProposalState.tariffItemSelectedRows]);

    const onRefreshRow = useCallback((newAllRows: any[]) => {
        setAllRows(newAllRows);
        standardProposalVM.updateSelectedTariffItemRows([]);
        setShowMoveCursor(true);
    }, [standardProposalVM]);

    const onMoveCancel = useCallback(() => {
        setInitialAllRows(true);
        setAllRows(standardProposalState.standardProposalItemList ?? []);
        standardProposalVM.updateSelectedTariffItemRows([]);
        setShowMoveCursor(false);
    }, [standardProposalState.standardProposalItemList, standardProposalVM]);

    const onSaveMove = useCallback(async (newAllRows: any[]) => {
        if (_.isEmpty(newAllRows)) return;

        setIsLoading(true);

        standardProposalVM.onMoveTariffItems(newAllRows).then((res) => {
            if (!res || !res.success) {
                messageBarVM.showError('Move failed.');
                setInitialAllRows(true);
                setIsLoading(false);

                standardProposalVM.updateSelectedTariffItemRows([]);
                setShowMoveCursor(false);
            } else {
                standardProposalVM.searchStandardProposalItem(currentSelectedRow).then((data) => {
                    setInitialAllRows(true);
                    setIsLoading(false);

                    standardProposalVM.updateSelectedTariffItemRows([]);
                    setShowMoveCursor(false);
                }).catch((e) => {
                    setInitialAllRows(true);
                    setIsLoading(false);

                    standardProposalVM.updateSelectedTariffItemRows([]);
                    setShowMoveCursor(false);
                })
            }
        })
    }, [currentSelectedRow, messageBarVM, standardProposalVM]);

    useEffect(() => {
        if (standardProposalState.allFormState && standardProposalState.allFormState["updateNewTariffNatureFail"]) {
            messageBarVM.showError(standardProposalState.allFormState["updateNewTariffNatureFail"]?.toString());
            standardProposalVM.onClearMessage();
        }
    }, [messageBarVM, standardProposalState.allFormState, standardProposalVM])

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        standardProposalVM.updateSelectedTariffItemRows(selectedRows);
    }, [standardProposalVM])

    const handleRowDoubleClick = useCallback((entity: StandardProposalItemEntity) => {
        standardProposalVM.onTariffItemRowDoubleClick(entity);
    }, [standardProposalVM])

    const handleAdd = useCallback(() => {
        if ("FINALIZED" === currentSelectedRow.status || "PROPOSED" === currentSelectedRow.status) {
            messageBarVM.showError("Standard Proposal status is \"FINALIZED\", cannot Add.");
            return;
        }
        standardProposalVM.onTariffItemAddClick();
    }, [currentSelectedRow.status, messageBarVM, standardProposalVM]);

    const handClose = useCallback(() => {
        standardProposalVM.onTariffItemCloseClick(isEditStdProposal);
    }, [standardProposalVM, isEditStdProposal]);

    const isDisable = useCallback(() => {
        if ((!(isAddIs || isAddTier || isEditIs || isEditTier) && (isAddTariffItem || isUpdateOrAdd) && isShowTariffItemTierPanel)
            || (deleteItemList && deleteItemList.length > 0 && !isShowTariffItemTierPanel)) {
            return true;
        }
        return false;

    }, [deleteItemList, isAddIs, isAddTariffItem, isAddTier, isEditIs, isEditTier, isShowTariffItemTierPanel, isUpdateOrAdd])

    const handEdit = useCallback(() => {
        if (currentSelectedRow.key === "") {
            messageBarVM.showError("Please select a record.");
            return;
        }
        if ("FINALIZED" === currentSelectedRow.status || "PROPOSED" === currentSelectedRow.status) {
            messageBarVM.showError("Standard Proposal status is \"FINALIZED\", cannot Edit.");
            return;
        }
        standardProposalVM.onTariffItemEidtClick();
    }, [currentSelectedRow.key, currentSelectedRow.status, standardProposalVM, messageBarVM]);

    const handDeleteClick = useCallback(() => {
        if ("FINALIZED" === currentSelectedRow.status || "PROPOSED" === currentSelectedRow.status) {
            messageBarVM.showError("Standard Proposal status is \"FINALIZED\", cannot Delete.");
            return;
        }
        if (tariffItemSelectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        // if (standardProposalItemList && standardProposalItemList.length === 1) {
        //     messageBarVM.showError("At least one tariff item is required.");
        //     return;
        // }
        standardProposalVM.onTariffItemDeleteClick(tariffItemSelectedRows);/*.then((res) => {
            setInitialAllRows(true);
            // setAllRows(standardProposalState.standardProposalItemList);
            setRefreshAllRowsAfterDelete(true);
            standardProposalVM.updateSelectedTariffItemRows([]);
        })*/
        setIsShowDelete(true);
    }, [currentSelectedRow.status, messageBarVM, standardProposalVM, tariffItemSelectedRows]);

    const handleCancel = () => {
        setIsShowDelete(false);
    }

    const handleDelete = useCallback(async () => {
        setIsShowDelete(false);

        //if (isUpdateOrAdd) {
        // if(currentTariffItem.tierList && currentTariffItem.tierList.length === 0){
        //     messageBarVM.showError("Must add one tariff item tier at least.");
        //     return;
        // }
        // standardProposalVM.onShowLoading();
        standardProposalVM.setIsLoadingTarItem(true);

        let tempDeleteItemList = !isShowTariffItemTierPanel ? deleteItemList : [];
        await standardProposalVM.onDeleteTariffItem(tempDeleteItemList).then(async (data) => {
            if (data && data.code !== "200") {
                setIsLoading(false);
                messageBarVM.showError(data.data);
                standardProposalVM.setIsLoadingTarItem(false);
                return;
            } else {
                setInitialAllRows(true);
                setRefreshAllRowsAfterDelete(true);

                if (searchStatus !== "") {
                    await standardProposalVM.onGroupButtonClick(searchStatus);
                } else {
                    await standardProposalVM.onTariffItemTierCloseClick();
                }
            }
        }).finally(() => {
            standardProposalVM.setIsLoadingTarItem(false);
        });
        //}

    }, [deleteItemList, isShowTariffItemTierPanel, messageBarVM, searchStatus, standardProposalVM]);


    const recordValidate = useCallback(() => {
        // if (!standardProposalState.currentSelectedRow.effectiveDate) {
        //     messageBarVM.showError("Effective Date is mandatory.");
        //     return false;
        // }
        // if (!standardProposalState.currentSelectedRow.expiryDate) {
        //     messageBarVM.showError("Expiry Date is mandatory.");
        //     return false;

        // }
        if (standardProposalState.currentSelectedRow.effectiveDate && standardProposalState.currentSelectedRow.expiryDate &&
            new Date(standardProposalState.currentSelectedRow.expiryDate) < new Date(standardProposalState.currentSelectedRow.effectiveDate)) {
            messageBarVM.showError("The Expiry Date should be later than the Effective Date.");
            return false;
        }
        // if (!standardProposalState.currentSelectedRow.currencyCode) {
        //     messageBarVM.showError("Currency is mandatory.");
        //     return false;
        // }

        // if (!standardProposalState.currentSelectedRow.tariffNatureList || standardProposalState.currentSelectedRow.tariffNatureList?.length === 0) {
        //     messageBarVM.showError("Tariff Nature is mandatory.");
        //     return false;
        // }
        return true;
    }, [messageBarVM, standardProposalState.currentSelectedRow.effectiveDate, standardProposalState.currentSelectedRow.expiryDate])


    const handleSave = useCallback(async () => {
        if (isEditStdProposal) {
            if (recordValidate()) {
                standardProposalVM.onShowLoading();
                standardProposalVM.onSaveClicked();
                await standardProposalVM.onSaveStdProposal(standardProposalState.currentSelectedRow).then(async (data) => {
                    if (data.code !== "200") {
                        messageBarVM.showError(data.data);
                        standardProposalVM.onHideLoading();
                        return;
                    } else {
                        messageBarVM.showSuccess("Save Successfully.");

                        if (searchStatus !== "") {
                            await standardProposalVM.onGroupButtonClick(searchStatus);
                        } else {
                            await standardProposalVM.searchStandardProposal(searchCriteria);
                        }
                    }
                    standardProposalVM.onHideLoading();
                });
            } else {
                return;
            }
        }
    }, [isEditStdProposal, messageBarVM, recordValidate, searchCriteria, searchStatus, standardProposalState.currentSelectedRow, standardProposalVM]);

    const memoBtns = useMemo(() => {

        const hdrBtns = [];
        if (!isShowTariffItemTierPanel) {
            if (allowCreate) {
                hdrBtns.push({
                    id: 'onAddClick',
                    icon: 'Icon-add',
                    title: 'Add',
                    disable: isEditStdProposal || deleteItemList.length > 0
                })
            }
            if (allowDelete) {
                hdrBtns.push({
                    id: 'onDeleteButton',
                    icon: 'Icon-trash',
                    title: 'Delete',
                    disable: isEditStdProposal
                })
            }

        }

        return hdrBtns
    }, [allowCreate, allowDelete, deleteItemList.length, isEditStdProposal, isShowTariffItemTierPanel])

    const handleCopyToNewProposalClick = useCallback(() => {
        if (!("FINALIZED" === currentSelectedRow.status && currentSelectedRow.confirmedDate)) {
            messageBarVM.showError("Only finalized proposal can be copy.");
            return;
        }
        if (tariffItemSelectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        standardProposalVM.onTariffItemCopyClick(true);
    }, [currentSelectedRow.confirmedDate, currentSelectedRow.status, messageBarVM, standardProposalVM, tariffItemSelectedRows.length]);

    const handleCopyToCombineProposalClick = useCallback(() => {
        if (!("FINALIZED" === currentSelectedRow.status && currentSelectedRow.confirmedDate)) {
            messageBarVM.showError("Only finalized proposal can be copy.");
            return;
        }
        if (tariffItemSelectedRows.length === 0) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        standardProposalVM.onTariffItemCopyClick(false);
    }, [currentSelectedRow.confirmedDate, currentSelectedRow.status, messageBarVM, standardProposalVM, tariffItemSelectedRows.length]);

    const isDisableCopyToNewProposal = useCallback(() => {
        if (!("FINALIZED" === currentSelectedRow.status && currentSelectedRow.confirmedDate)) {
            return true;
        }
    }, [currentSelectedRow.confirmedDate, currentSelectedRow.status]);

    const isDisableGenerate = useCallback(() => {
        if (isEditStdProposal || isShowTariffItemTierPanel
            || ("FINALIZED" === currentSelectedRow.status)) {
            return true;
        }
    }, [currentSelectedRow.status, isEditStdProposal, isShowTariffItemTierPanel]);

    const isEditDisableButton = useCallback(() => {
        if (isEditStdProposal || isShowTariffItemTierPanel) {
            return true;
        }
    }, [isEditStdProposal, isShowTariffItemTierPanel]);

    const isRowSelectable = useCallback(() => {
        return !(isShowTariffItemTierPanel || isEditStdProposal)
    }, [isEditStdProposal, isShowTariffItemTierPanel]);


    const memoCompanyCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.companyCode || ''}
                fieldValue={currentSelectedRow?.companyCode}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.COMPANY_CODE}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'companyCode'}
                maxLength={60}
                isLimitOptions={true}
                options={standardProposalState.dynamicOptions.companyCodeDropdownOptions}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.COMPANY_CODE, allFormState, currentSelectedRow?.companyCode, isSaveClicked, standardProposalState.dynamicOptions.companyCodeDropdownOptions, standardProposalVM])

    const memoOperatingTml = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditStdProposal}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.operatingTml || ''}
                fieldValue={currentSelectedRow?.operatingTml}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.OPERATING_TML}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'operatingTml'}
                maxLength={60}
                options={standardProposalState.dynamicOptions.operatingTmlDropdownOptions}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.OPERATING_TML, allFormState, currentSelectedRow?.operatingTml, isEditStdProposal, isSaveClicked, standardProposalState.dynamicOptions.operatingTmlDropdownOptions, standardProposalVM])

    const memoEffectiveDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <DatePickerComponent
                disabled={!isEditStdProposal}
                label={STANDARD_PROPOSAL_CONSTANT.EFFECTIVE_DATE}
                width="185px"
                date={currentSelectedRow?.effectiveDate}
                fieldName="effectiveDate"
                errorMessage={allFormState ? allFormState["effectiveDate"] ?? "" : ""}
                onDateChange={standardProposalVM.onHeaderFieldChange} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.EFFECTIVE_DATE, allFormState, currentSelectedRow?.effectiveDate, isEditStdProposal, standardProposalVM.onHeaderFieldChange])

    const memoExpiryDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <DatePickerComponent
                disabled={!isEditStdProposal}
                label={STANDARD_PROPOSAL_CONSTANT.EXPIRY_DATE}
                width="185px"
                date={currentSelectedRow?.expiryDate}
                fieldName="expiryDate"
                errorMessage={allFormState ? allFormState["expiryDate"] ?? "" : ""}
                onDateChange={standardProposalVM.onHeaderFieldChange} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.EXPIRY_DATE, allFormState, currentSelectedRow?.expiryDate, isEditStdProposal, standardProposalVM.onHeaderFieldChange])

    const memoCurrency = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditStdProposal}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.currencyCode || ''}
                fieldValue={currentSelectedRow?.currencyCode}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.CURRENCY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'currencyCode'}
                maxLength={60}
                options={standardProposalState.dynamicOptions.currencyCodeDropdownOptions}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.CURRENCY, allFormState, currentSelectedRow?.currencyCode, isEditStdProposal, isSaveClicked, standardProposalState.dynamicOptions.currencyCodeDropdownOptions, standardProposalVM])

    const memoProposalBy = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.proposalCreatedByMethod || ''}
                fieldValue={currentSelectedRow?.proposalCreatedByMethod}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.PROPOSAL_BY}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'proposalCreatedByMethod'}
                maxLength={60}
                options={proposalByDropdownOption}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.PROPOSAL_BY, allFormState, currentSelectedRow?.proposalCreatedByMethod, isSaveClicked, standardProposalVM])

    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.status || ''}
                fieldValue={currentSelectedRow?.status}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.STATUS}
                isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'status'}
                maxLength={60}
                options={approvalProcessStatusDropdownOption}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.STATUS, allFormState, currentSelectedRow?.status, isSaveClicked, standardProposalVM])

    const memoProposalNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.proposalNo || ''}
                fieldValue={currentSelectedRow?.proposalNo}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.PROPOSAL_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'proposalNo'}
                maxLength={7}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.PROPOSAL_NO, allFormState, currentSelectedRow?.proposalNo, standardProposalVM])

    const memoNature = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditStdProposal}
                isShowOptional={false}
                // readOnlyValue={currentSelectedRow?.tariffNature || ''}
                readOnlyValue={currentSelectedRow?.tariffNature && currentSelectedRow?.tariffNature?.length <= 20 ? currentSelectedRow?.tariffNature : (currentSelectedRow?.tariffNature?.substring(0, 20) + '...') || ''}
                fieldValue={currentSelectedRow?.tariffNatureList}
                allValues={currentSelectedRow?.tariffNature ?? ""}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.TARIFF_NATURE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'tariffNatureList'}
                maxLength={600}
                isMultipleDropDown={true}
                options={standardProposalState.dynamicOptions.tariffNatureDropdownOptions}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onTariffNatureFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.TARIFF_NATURE, allFormState, currentSelectedRow?.tariffNature, currentSelectedRow?.tariffNatureList, isEditStdProposal, standardProposalState.dynamicOptions.tariffNatureDropdownOptions, standardProposalVM])

    const memoConfirmedDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }}>
            <DatePickerComponent
                disabled={true}
                label={STANDARD_PROPOSAL_CONSTANT.CONFIRMED_DATE}
                width="185px"
                date={currentSelectedRow?.confirmedDate}
                fieldName="confirmedDate"
                errorMessage={""}
                onDateChange={() => { }} />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.CONFIRMED_DATE, currentSelectedRow?.confirmedDate])

    const memoRejectReason = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "185px", marginBottom: "24px", marginLeft: "15px" }} >
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={true}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.rejectReason || ''}
                fieldValue={currentSelectedRow?.rejectReason}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.REJECT_REASON}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'rejectReason'}
                maxLength={1333}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)}
            />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.REJECT_REASON, allFormState, currentSelectedRow?.rejectReason, standardProposalVM])

    const memoRemarks = useMemo(() =>
        <div className='im-flex-row-item' id="std-proposal-edit-remarks" style={{ width: "405px", marginBottom: "24px", marginLeft: "15px" }} >
            <NbisCommonField
                errorMessages={allFormState}
                isReadOnly={!isEditStdProposal}
                isShowOptional={true}
                readOnlyValue={currentSelectedRow?.remarks || ''}
                fieldValue={currentSelectedRow?.remarks}
                fieldLabel={STANDARD_PROPOSAL_CONSTANT.REMARKS}
                isSaveClicked={false}
                fieldType={FieldType.TEXTAREA}
                fieldKey={'remarks'}
                maxLength={1333}
                rows={1}
                requiredFieldList={standardProposalRequiredFieldList}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    standardProposalVM.onHeaderTextAreaChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)}
            />
        </div>
        , [STANDARD_PROPOSAL_CONSTANT.REMARKS, allFormState, currentSelectedRow?.remarks, isEditStdProposal, standardProposalVM])


    const handleMaster = useCallback(async () => {
        standardProposalVM.onMasterOrAllianceClick("MASTER", currentSelectedRow).then(data => {
            if (data) {
                messageBarVM.showError(data);
            }
        });
    }, [currentSelectedRow, messageBarVM, standardProposalVM]);

    const handleAlliance = useCallback(async () => {
        standardProposalVM.onMasterOrAllianceClick("ALLIANCE", currentSelectedRow).then(data => {
            if (data) {
                messageBarVM.showError(data);
            }
        });
    }, [currentSelectedRow, messageBarVM, standardProposalVM]);

    const handleBack = useCallback(async () => {
        standardProposalVM.onMasterOrAllianceClick("PreviousCompany", currentSelectedRow).then(data => {
            if (data) {
                messageBarVM.showError(data);
            }
        });
    }, [currentSelectedRow, messageBarVM, standardProposalVM]);

    const handleGenerate = useCallback(async () => {
        standardProposalVM.setIsLoadingTarItem(true);
        await standardProposalVM.onGenerateClick(standardProposalItemList ?? [], currentSelectedRow).then(async data => {
            if (data.success) {
                messageBarVM.showSuccess("Generate Successfully");
            } else {
                messageBarVM.showError(data.data);
            }
        }).finally(() => {
            standardProposalVM.setIsLoadingTarItem(false);
        });
    }, [currentSelectedRow, messageBarVM, standardProposalItemList, standardProposalVM]);

    const memoTableTitle = useMemo(() => {
        const copyItemList: OverflowingSubMenu[] = [{
            title: 'New',
            command: handleCopyToNewProposalClick
        },
        {
            title: 'Combine',
            command: handleCopyToCombineProposalClick
        }
        ];
        return (
            <>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    {(allowUpdate) && <>
                        <div style={{ marginRight: !isMasterOrAlliance ? "5px" : "0px" }}>
                            {!isMasterOrAlliance && <HPHButton disabled={isEditDisableButton()} label={STANDARD_PROPOSAL_CONSTANT.BUT_MASTER} size={'Small'} theme={'Secondary'} onClick={handleMaster} />}
                        </div>
                        <div style={{ marginRight: !isMasterOrAlliance ? "5px" : "0px" }}>
                            {!isMasterOrAlliance && <HPHButton disabled={isEditDisableButton()} label={STANDARD_PROPOSAL_CONSTANT.BUT_ALLIANCE} size={'Small'} theme={'Secondary'} onClick={handleAlliance} />}
                        </div>
                    </>}
                    <div style={{ marginRight: isMasterOrAlliance ? "5px" : "0px" }}>
                        {isMasterOrAlliance && <HPHButton disabled={standardProposalItemList?.length === 0 || isEditDisableButton()} label={STANDARD_PROPOSAL_CONSTANT.BUT_BACK} size={'Small'} theme={'Secondary'} onClick={handleBack} />}
                    </div>

                    {(allowUpdate) && <>
                        <div style={{ marginRight: "5px" }}>
                            <HPHButton disabled={_.isEmpty(standardProposalState.standardProposalItemList) || isDisableGenerate()} label={STANDARD_PROPOSAL_CONSTANT.BUT_GENERATE} size={'Small'} theme={'Secondary'} onClick={handleGenerate} />
                        </div>
                    </>}
                    {(allowCreate) && <OverflowMenuButton disabled={isDisableCopyToNewProposal()} icon="Icon-copy" toolTipText="Copy" navigationList={copyItemList} className={"title-overflowMenu-forStdProposalCopy"} />}
                </div>
            </>
        )
    }, [STANDARD_PROPOSAL_CONSTANT.BUT_ALLIANCE, STANDARD_PROPOSAL_CONSTANT.BUT_BACK, STANDARD_PROPOSAL_CONSTANT.BUT_GENERATE, STANDARD_PROPOSAL_CONSTANT.BUT_MASTER, allowCreate, allowUpdate, handleAlliance, handleBack, handleCopyToCombineProposalClick, handleCopyToNewProposalClick, handleGenerate, handleMaster, isDisableCopyToNewProposal, isDisableGenerate, isEditDisableButton, isMasterOrAlliance, standardProposalItemList?.length, standardProposalState.standardProposalItemList])



    const memoStandardProposalTariffItemTable = useMemo(() => {
        // const copyItemList: OverflowingSubMenu[] = [{
        //     title: 'New',
        //     command: handleCopyToNewProposalClick
        // },
        // {
        //     title: 'Combine',
        //     command: handleCopyToCombineProposalClick
        // }
        // ];

        return (
            <>
                {(allowUpdate) && <RightClickMenuWithMoveAfter disabled={_.isEmpty(getSelectedRows())}
                    rightClickRef={rightClickRef} selectedRows={getSelectedRows()} allRows={getAllRows()}
                    onSaveMove={onSaveMove} onCancel={onMoveCancel} onRefreshRow={onRefreshRow}
                    showConfirmMove={showMoveCursor} priorityName="priority" />}
                <div id="myDiv">
                    <NbisTable
                        id='standard-proposal-detail-table'
                        isNewColumnSetting={true}
                        columns={INITIAL_STANDARD_PROPOSAL_DETAIL_COL_DEF?.slice()}
                        data={transferRowData(allRows ?? [])}
                        headerActionButtons={memoBtns}
                        onDeleteButton={(allowDelete) && handDeleteClick}
                        showPaginator={false}
                        editable={false}
                        showAddIcon={false}
                        onAddClick={(allowCreate) && handleAdd}
                        showDeleteButton={false}
                        showUploadIcon={false}
                        showReloadIcon={false}
                        isScrollHighlighted={true}
                        selectionMode={false}
                        rowSelection={showMoveCursor ? "single" : "multiple"}
                        isRowHighligted={true}
                        handleSelectionChanged={handleSelectionChange}
                        onRowDoubleClick={(e: any, entity: StandardProposalItemEntity) => handleRowDoubleClick(entity)}
                        gridHeight="customHeight"
                        customHeight={/*isShowTariffItemTierPanel ? "calc(100vh - 535px)" : */"calc(50vh - 19px)"}
                        ref={gridRef}
                        headerLabel={!isShowTariffItemTierPanel ? memoTableTitle : ""}
                        messageVM={messageBarVM}
                        handleRowSelectable={isRowSelectable}
                        showCutCursor={showMoveCursor}
                        isLoading={standardProposalState.isLoadingTarItem}
                    />

                    {standardProposalState.forceRefresh && <></>}
                </div>
            </>
        );
    }, [allowUpdate, getSelectedRows, rightClickRef, getAllRows, onSaveMove, onMoveCancel, onRefreshRow, showMoveCursor, allRows, memoBtns, allowDelete, handDeleteClick, allowCreate, handleAdd, handleSelectionChange, isShowTariffItemTierPanel, memoTableTitle, messageBarVM, isRowSelectable, standardProposalState.isLoadingTarItem, standardProposalState.forceRefresh, handleRowDoubleClick])

    useEffect(() => {
        console.log('State changed:', {
            standardProposalItemList: standardProposalState.standardProposalItemList,
            allRows: allRows,
            initialAllRows: initialAllRows,
            initialProposalItem: standardProposalState.initialProposalItem
        });
    }, [standardProposalState.standardProposalItemList, allRows, standardProposalState.initialProposalItem, initialAllRows]);

    // 检查gridRef
    useEffect(() => {
        console.log('gridRef:', gridRef.current);
    }, []);

    useEffect(() => {
        if (initialAllRows && standardProposalState.standardProposalItemList && !_.isEmpty(standardProposalState.standardProposalItemList)) {
            setAllRows(standardProposalState.standardProposalItemList.map((standardProposalItemEntity, index) => ({
                ...standardProposalItemEntity,
                index: index || 0
            })));
            setInitialAllRows(false);
            standardProposalVM.resetInitialProposalItem();
        } else if (refreshAllRowsAfterDelete) {
            setAllRows(standardProposalState.standardProposalItemList ?? [])
            setRefreshAllRowsAfterDelete(false);
        }
    }, [standardProposalState.initialProposalItem, allRows, initialAllRows, refreshAllRowsAfterDelete, standardProposalState.standardProposalItemList, standardProposalVM]);

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            gridRef.current.gridRef.current.api.redrawRows();
        }
    }, [isShowTariffItemTierPanel, isEditStdProposal, standardProposalState.isAddIs, standardProposalState.isAddTier, standardProposalState.isEditIs, standardProposalState.isEditTier])



    return <><div className={`main-comp-wrapper`}>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        {/* <TableWrapper> */}
        {/* <div style={{ height: (isShowTariffItemTierPanel ? '45%' : '35%') }}> */}
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <SidebarTitle></SidebarTitle>
                    <SidebarActionCross>
                        {(allowUpdate) && !isEditStdProposal && !isShowTariffItemTierPanel && !isMasterOrAlliance && <IconButton
                            fileName="Icon-pen" size="medium" toolTipText='Edit' toolTipArrow={isDisable()} onClick={handEdit} />}
                        {!isShowTariffItemTierPanel && <IconButton
                            fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={handClose} />}
                        {(allowUpdate) && isEditStdProposal && !isShowTariffItemTierPanel && <IconButton
                            fileName="Icon-tick" size="medium" toolTipText='Save' toolTipArrow={false} onClick={handleSave} />}
                    </SidebarActionCross>
                </Sidebarheader>
            </div>
        </div>
        <Sidebarheader style={{ width: '100%' }}>
            <div className={'add-edit-form'} style={{ height: '30vh', maxHeight: '30vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoCompanyCode}
                            {memoProposalNo}
                            {memoStatus}
                            {memoProposalBy}
                            {memoEffectiveDate}
                            {memoExpiryDate}
                            {memoOperatingTml}
                            {memoCurrency}
                            {memoNature}
                            {memoConfirmedDate}
                            {memoRejectReason}
                            {memoRemarks}
                        </CriteriaItemContainer>
                    </div>
                </div>
            </div>
        </Sidebarheader>
        {/* </div> */}
        {/* <div style={{ height: (isShowTariffItemTierPanel ? '55%' : '65%') }}> */}
        <TableWrapper>
            {standardProposalItemList === null || standardProposalItemList === undefined || memoStandardProposalTariffItemTable}
        </TableWrapper>
        {/* </div> */}
        {/* </TableWrapper>*/}
        {standardProposalState.forceRefresh && <></>}
        <StyledAction className="tm-animated-wrapper">
            <ConfirmDeleteModal headerText={`${WorkspaceConstant.Common.BUTTON_DELETE} ${StandardProposalConstant.Title.STANDARD_PROPOSAL_ITEM}`}
                contentText={`${MessageConstant.common.DELETE_ALERT_TITLE}\n${MessageConstant.common.DELETE_ALERT_MESSAGE}`}
                visible={isShowDelete}
                onCancelClick={handleCancel}
                onDeleteClick={handleDelete}
            />
        </StyledAction>
    </div></>;
}

export default memo(StandardProposalTariffItemTablePanel);
